import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, RefreshCw } from 'lucide-react';
import { Button } from '../components/ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const statusOptions = ['pending', 'confirmed', 'cancelled'];

export default function AdminBookingsPage() {
  const { websiteId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);
  const [error, setError] = useState('');
  const [items, setItems] = useState([]);

  const token = useMemo(() => localStorage.getItem('token'), []);

  const fetchBookings = async () => {
    setError('');
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/sites/${websiteId}/resources/bookings`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.detail || 'Failed to load bookings');
      setItems(data.items || []);
    } catch (e) {
      setError(e?.message || 'Failed to load bookings');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (websiteId) fetchBookings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [websiteId]);

  const updateStatus = async (bookingId, status) => {
    setSavingId(bookingId);
    setError('');
    try {
      const res = await fetch(`${API_URL}/api/sites/${websiteId}/resources/bookings/${bookingId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ status }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.detail ? JSON.stringify(data.detail) : 'Failed to update status');

      // Optimistic update
      setItems((prev) => prev.map((b) => (b.id === bookingId ? data.item : b)));
    } catch (e) {
      setError(e?.message || 'Failed to update status');
    } finally {
      setSavingId(null);
    }
  };

  return (
    <div className="min-h-screen p-6 md:p-12">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <Button variant="ghost" className="text-zinc-400 hover:text-white" onClick={() => navigate(`/admin/${websiteId}`)}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>

          <Button variant="outline" className="gap-2" onClick={fetchBookings} disabled={loading}>
            <RefreshCw className="w-4 h-4" />
            Refresh
          </Button>
        </div>

        <h1 className="text-4xl md:text-5xl font-heading font-extrabold mb-2">Reservations</h1>
        <p className="text-zinc-400 mb-8">Admin-only. Update reservation status.</p>

        {error ? (
          <div className="mb-6 rounded-xl border border-red-500/40 bg-red-500/10 p-4 text-sm text-red-200">{error}</div>
        ) : null}

        <div className="rounded-2xl border border-zinc-800 bg-zinc-950/40 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-zinc-900/60 text-zinc-200">
                <tr>
                  <th className="text-left px-4 py-3">Guest</th>
                  <th className="text-left px-4 py-3">Email</th>
                  <th className="text-left px-4 py-3">Party</th>
                  <th className="text-left px-4 py-3">Time</th>
                  <th className="text-left px-4 py-3">Status</th>
                  <th className="text-left px-4 py-3">Update</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-800">
                {loading ? (
                  <tr>
                    <td className="px-4 py-4 text-zinc-400" colSpan={6}>
                      Loading…
                    </td>
                  </tr>
                ) : items.length ? (
                  items.map((b) => (
                    <tr key={b.id} className="text-zinc-200">
                      <td className="px-4 py-4">{b.guest_name}</td>
                      <td className="px-4 py-4">{b.email}</td>
                      <td className="px-4 py-4">{b.party_size}</td>
                      <td className="px-4 py-4">{b.time}</td>
                      <td className="px-4 py-4">
                        <span className="inline-flex items-center rounded-full bg-zinc-800 px-2 py-1 text-xs text-zinc-200">
                          {b.status || 'pending'}
                        </span>
                      </td>
                      <td className="px-4 py-4">
                        <select
                          className="rounded-lg border border-zinc-700 bg-zinc-900 px-2 py-1 text-zinc-200"
                          value={b.status || 'pending'}
                          disabled={savingId === b.id}
                          onChange={(e) => updateStatus(b.id, e.target.value)}
                        >
                          {statusOptions.map((s) => (
                            <option key={s} value={s}>
                              {s}
                            </option>
                          ))}
                        </select>
                        {savingId === b.id ? (
                          <span className="ml-2 text-xs text-zinc-400">Saving…</span>
                        ) : null}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td className="px-4 py-4 text-zinc-400" colSpan={6}>
                      No bookings yet.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
